import { useEffect, useMemo, useState } from "react";

// mui icons
import CloseIcon from "@mui/icons-material/Close";

// mui components
import { Stack, InputLabel, Box } from "@mui/material";

// custom components
import Heading3 from "../../../typography/heading-03";
import Heading6 from "../../../typography/heading-06";
import SecondaryButton from "../../../buttons/secondary-button";
import PrimaryButton from "../../../buttons/primary-button";
import OutlinedTextInput from "../../../custom-input/outlined-text-input";
import OutlinedTextarea from "../../../custom-input/outlined-textarea";
import SecondarySelect from "../../../custom-select/secondary-select";
import CustomCheckbox from "../../../custom-checkbox";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";

// api slice
import { useEditMessageMutation } from "../../../../features/messages/messagesApiSlice";

// helper utils
import {
	MESSAGE_DRAWER_TYPES,
	MESSAGE_ROLES,
	MESSAGE_TYPES,
} from "../../../../utils/messages.constants";
import { messageTypeOptions } from "../../../../utils/select-options";
import {
	rolesToString,
	stringToRoles,
} from "../../../../utils/role-transformer";

// custom styles
import styles from "./index.module.scss";
import "react-datepicker/dist/react-datepicker.css";
import "../request-closing-date/styles.css";

const UpdateMessageDrawer = ({
	handleCloseDrawer,
	handleOpenProcessingModal,
	handleCloseProcessingModal,
	handleOpenSuccessModal,
	setProcessingText,
	setSuccessText,
	handleOpenCommonErrorModal,
	toggleDrawer,
	drawerData,
	drawerType,
	refreshMessages,
}) => {
	const [editMessage, { isLoading: isEditingMessage }] =
		useEditMessageMutation();

	// Check if the message being edited is expired
	const isMessageExpired = useMemo(() => {
		if (!drawerData?.end_date) return false;
		const currentDate = DateTime.now().setZone("utc");
		const endDate = DateTime.fromISO(drawerData.end_date, { zone: "utc" });
		return endDate.isValid && endDate < currentDate;
	}, [drawerData?.end_date]);

	const [subject, setSubject] = useState("");
	const [details, setDetails] = useState("");
	const [category, setCategory] = useState("");
	const [selectedRoles, setSelectedRoles] = useState([]);
	const [startAt, setStartAt] = useState(null);
	const [endAt, setEndAt] = useState(null);

	useEffect(() => {
		if (drawerData) {
			setSubject(drawerData?.title || "");
			setDetails(drawerData?.body || "");
			setCategory(drawerData?.message_type || "");
			setSelectedRoles(stringToRoles(drawerData?.role) || []);
			if (drawerData?.start_date) {
				const startDate = DateTime.fromISO(drawerData.start_date, {
					zone: "utc",
				});
				if (startDate.isValid) {
					// Create a local Date with the same values as UTC for Datepicker
					const utcDate = new Date(
						startDate.year,
						startDate.month - 1,
						startDate.day,
						startDate.hour,
						startDate.minute,
						startDate.second
					);
					setStartAt(utcDate);
				}
			}
			if (drawerData?.end_date) {
				const endDate = DateTime.fromISO(drawerData.end_date, { zone: "utc" });
				if (endDate.isValid) {
					// Create a local Date with the same values as UTC for Datepicker
					const utcDate = new Date(
						endDate.year,
						endDate.month - 1,
						endDate.day,
						endDate.hour,
						endDate.minute,
						endDate.second
					);
					setEndAt(utcDate);
				}
			}
		}
	}, [drawerData]);

	const resetForm = () => {
		setSubject("");
		setDetails("");
		setCategory("");
		setSelectedRoles([]);
		setStartAt(null);
		setEndAt(null);
		handleCloseDrawer();
	};

	const getCurrentUtcAsLocalDate = () => {
		const nowUtc = DateTime.now().setZone("utc");
		return new Date(
			nowUtc.year,
			nowUtc.month - 1,
			nowUtc.day,
			nowUtc.hour,
			nowUtc.minute,
			nowUtc.second
		);
	};

	const minStartDate = useMemo(() => getCurrentUtcAsLocalDate(), []);

	const getMinEndDate = useMemo(() => {
		return startAt ? startAt : getCurrentUtcAsLocalDate();
	}, [startAt]);

	const getMinTime = (date) => {
		const nowUtc = getCurrentUtcAsLocalDate();
		if (!date) return new Date(0, 0, 0, 0, 0, 0);

		const selectedDateLocal = DateTime.fromJSDate(date);
		const nowUtcLocal = DateTime.fromJSDate(nowUtc);
		const isToday = selectedDateLocal.hasSame(nowUtcLocal, "day");

		return isToday ? nowUtc : new Date(0, 0, 0, 0, 0, 0);
	};

	const getMaxTime = () => new Date(0, 0, 0, 23, 59, 59);

	const handleSubmit = async () => {
		// Validations
		if (!subject.trim()) {
			handleOpenCommonErrorModal("ADMIN_SUBJECT_REQUIRED");
			return;
		}

		if (!details.trim()) {
			handleOpenCommonErrorModal("ADMIN_DETAILS_REQUIRED");
			return;
		}

		if (!category) {
			handleOpenCommonErrorModal("ADMIN_CATEGORY_REQUIRED");
			return;
		}

		if (!startAt) {
			handleOpenCommonErrorModal("ADMIN_START_DATETIME_REQUIRED");
			return;
		}

		if (!endAt) {
			handleOpenCommonErrorModal("ADMIN_END_DATETIME_REQUIRED");
			return;
		}

		// date validations
		if (DateTime.fromJSDate(endAt) < DateTime.fromJSDate(startAt)) {
			handleOpenCommonErrorModal("ADMIN_END_DATETIME_INVALID");
			return;
		}

		if (
			category === MESSAGE_TYPES.ROLE_SPECIFIC &&
			selectedRoles.length === 0
		) {
			handleOpenCommonErrorModal("ADMIN_ROLE_REQUIRED");
			return;
		}

		try {
			setProcessingText("Updating message...");
			handleOpenProcessingModal();

			let formData = new FormData();
			if (subject !== drawerData?.title) {
				formData.append("title", subject);
			}
			if (details !== drawerData?.body) {
				formData.append("body", details);
			}
			if (category !== drawerData?.message_type) {
				formData.append("message_type", category);
			}

			if (
				category === MESSAGE_TYPES.ROLE_SPECIFIC &&
				rolesToString(selectedRoles) !== drawerData?.role
			) {
				formData.append("role", rolesToString(selectedRoles));
			} else if (
				category === MESSAGE_TYPES.GENERAL &&
				drawerData?.role !== rolesToString(MESSAGE_ROLES)
			) {
				formData.append("role", rolesToString(MESSAGE_ROLES));
			}

			// Convert the local Date object back to UTC ISO string for submission
			const startFormatted = DateTime.fromObject(
				{
					year: startAt.getFullYear(),
					month: startAt.getMonth() + 1,
					day: startAt.getDate(),
					hour: startAt.getHours(),
					minute: startAt.getMinutes(),
					second: startAt.getSeconds(),
				},
				{ zone: "utc" }
			).toISO();

			const endFormatted = DateTime.fromObject(
				{
					year: endAt.getFullYear(),
					month: endAt.getMonth() + 1,
					day: endAt.getDate(),
					hour: endAt.getHours(),
					minute: endAt.getMinutes(),
					second: endAt.getSeconds(),
				},
				{ zone: "utc" }
			).toISO();

			if (
				DateTime.fromISO(startFormatted).toISO({
					suppressMilliseconds: true,
					includeOffset: false,
				}) !==
				DateTime.fromISO(drawerData?.start_date).toISO({
					suppressMilliseconds: true,
					includeOffset: false,
				})
			) {
				formData.append("start_date", startFormatted);
			}

			if (
				DateTime.fromISO(endFormatted).toISO({
					suppressMilliseconds: true,
					includeOffset: false,
				}) !==
				DateTime.fromISO(drawerData?.end_date).toISO({
					suppressMilliseconds: true,
					includeOffset: false,
				})
			) {
				formData.append("end_date", endFormatted);
			}

			let response = await editMessage({
				id: drawerData.id,
				formData: formData,
			}).unwrap();
			handleCloseProcessingModal();

			if (response && response?.id) {
				setSuccessText("Message updated successfully");
				handleOpenSuccessModal();

				if (refreshMessages) {
					refreshMessages();
				}
				resetForm();
			} else {
				handleOpenCommonErrorModal("ADMIN_MESSAGE_UPDATE_FAILED");
			}
		} catch (err) {
			handleCloseProcessingModal();
			handleOpenCommonErrorModal("ADMIN_MESSAGE_UPDATE_FAILED");
		}
	};

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				className={styles.drawerHeaderContainer}
			>
				<Heading3
					text={"Edit Message"}
					extraClass={styles.headerText}
					fontType="semibold"
				/>
				<Stack direction="row" gap={2} alignItems="center">
					<SecondaryButton
						text="Edit History"
						onClick={() => {
							toggleDrawer(
								null,
								MESSAGE_DRAWER_TYPES.MESSAGE_HISTORY,
								drawerData
							);
						}}
					/>
					<SecondaryButton
						startIcon={<CloseIcon />}
						text="Close"
						onClick={handleCloseDrawer}
					/>
				</Stack>
			</Stack>

			<Stack direction="column" className={styles.drawerBodyContainer}>
				{/* Subject */}
				<Stack direction="column" className={styles.inputContainer}>
					<InputLabel htmlFor="subject-input">
						<Heading6
							text="Subject"
							fontType="semibold"
							extraClass={styles.inputHeader}
						/>
					</InputLabel>
					<OutlinedTextInput
						id="subject-input"
						autoComplete="off"
						placeholder="Enter Subject"
						value={subject}
						handleChange={(event) => setSubject(event.target.value)}
						fullWidth={true}
						disabled={isMessageExpired}
					/>
				</Stack>

				{/* Details */}
				<Stack direction="column" className={styles.inputContainer}>
					<InputLabel htmlFor="details-input">
						<Heading6
							text="Details"
							fontType="semibold"
							extraClass={styles.inputHeader}
						/>
					</InputLabel>
					<OutlinedTextarea
						id="details-input"
						autoComplete="off"
						placeholder="Enter Details"
						value={details}
						handleChange={(event) => setDetails(event.target.value)}
						fullWidth={true}
						rows={4}
						disabled={isMessageExpired}
					/>
				</Stack>

				{/* Category */}
				<Stack direction="column" className={styles.inputContainer}>
					<InputLabel htmlFor="category-select">
						<Heading6
							text="Category"
							fontType="semibold"
							extraClass={styles.inputHeader}
						/>
					</InputLabel>
					<SecondarySelect
						id="category-select"
						displayEmpty={true}
						placeholder="Select"
						fullWidth={true}
						value={category}
						handleChange={(event) => setCategory(event.target.value)}
						options={messageTypeOptions}
						disabled={isMessageExpired}
					/>
				</Stack>

				{/* Role selection (only when Role Specific) */}
				{category === MESSAGE_TYPES.ROLE_SPECIFIC && (
					<Stack direction="column" className={styles.rolesContainer}>
						<Heading6 text="Select Role(s):" fontType="semibold" />
						<Box className={styles.rolesGrid}>
							{MESSAGE_ROLES.map((role) => (
								<CustomCheckbox
									key={role}
									label={role}
									checked={selectedRoles.includes(role)}
									onChange={() => {
										setSelectedRoles((prev) =>
											prev.includes(role)
												? prev.filter((r) => r !== role)
												: [...prev, role]
										);
									}}
									disabled={isMessageExpired}
									sx={{
										opacity: isMessageExpired ? 0.38 : 1,
									}}
								/>
							))}
						</Box>
					</Stack>
				)}

				{/* Date/Time inputs */}
				<Stack direction="row" className={styles.dateTimeContainer}>
					{/* Start Date/Time */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="start-datetime-picker">
							<Heading6
								text="Start DateTime (UTC)"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>
						<Stack
							sx={{
								"& .react-datepicker-wrapper": {
									"& .react-datepicker__input-container input": {
										opacity: isMessageExpired ? 0.7 : 1,
									},
								},
							}}
						>
							<DatePicker
								id="start-datetime-picker"
								autoComplete="off"
								className={styles.datepicker}
								selected={startAt}
								onChange={(date) => {
									setStartAt(date);
									if (
										endAt &&
										date &&
										DateTime.fromJSDate(endAt) < DateTime.fromJSDate(date)
									) {
										setEndAt(date);
									}
								}}
								showTimeSelect
								timeIntervals={15}
								timeFormat="HH:mm"
								dateFormat="LLL dd yyyy, HH:mm 'UTC'"
								minDate={minStartDate}
								minTime={getMinTime(startAt || getCurrentUtcAsLocalDate())}
								maxTime={getMaxTime()}
								placeholderText="Select start date/time"
								disabled={isMessageExpired}
							/>
						</Stack>
					</Stack>

					{/* End Date/Time */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="end-datetime-picker">
							<Heading6
								text="End DateTime (UTC)"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>
						<Stack
							sx={{
								"& .react-datepicker-wrapper": {
									"& .react-datepicker__input-container input": {
										opacity: isMessageExpired ? 0.7 : 1,
									},
								},
							}}
						>
							<DatePicker
								id="end-datetime-picker"
								autoComplete="off"
								className={styles.datepicker}
								selected={endAt}
								onChange={(date) => setEndAt(date)}
								showTimeSelect
								timeIntervals={15}
								timeFormat="HH:mm"
								dateFormat="LLL dd yyyy, HH:mm 'UTC'"
								minDate={getMinEndDate}
								minTime={getMinTime(
									endAt || startAt || getCurrentUtcAsLocalDate()
								)}
								maxTime={getMaxTime()}
								placeholderText="Select end date/time"
								disabled={isMessageExpired}
							/>
						</Stack>
					</Stack>
				</Stack>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					className={styles.drawerFooterContainer}
				>
					<SecondaryButton
						text="Cancel"
						onClick={resetForm}
						extraClass={styles.cancelBtn}
					/>
					<PrimaryButton
						text="Update Message"
						onClick={handleSubmit}
						disabled={isEditingMessage || isMessageExpired}
					/>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default UpdateMessageDrawer;
